import { useCallback, useEffect, useRef, useState } from 'react';
import useClickOutside from 'ui/src/hooks/useClickOutside';

export default function useSearch() {
  const [showSearch, setShowSearch] = useState(false);
  const [isSearchInputFocused, setIsSearchInputFocused] = useState(false);
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!navRef.current) return () => {};
    const searchInput = navRef.current?.getElementsByTagName('input')?.[0];
    if (!searchInput) return () => {};
    const focusInListener = () => setIsSearchInputFocused(true);
    const focusOutListener = () => setIsSearchInputFocused(false);
    searchInput.addEventListener('focusin', focusInListener);
    searchInput.addEventListener('focusout', focusOutListener);
    return () => {
      searchInput.removeEventListener('focusin', focusInListener);
      searchInput.removeEventListener('focusout', focusOutListener);
    };
  }, []);

  const { clickedOutside } = useClickOutside(navRef);

  if (clickedOutside && showSearch) {
    setShowSearch(false);
  }

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      setShowSearch(false);
    }
  }, []);

  const onBlur = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      if (showSearch && !navRef.current?.contains(e.relatedTarget as Node)) {
        setShowSearch(false);
      }
    },
    [showSearch],
  );

  const openSearch = useCallback(() => {
    setShowSearch(true);
  }, []);

  useEffect(() => {
    if (showSearch) {
      navRef.current?.getElementsByTagName('input')?.[0]?.focus();
    }
  }, [showSearch]);

  return {
    showSearch,
    openSearch,
    navRef,
    onKeyDown,
    onBlur,
    isSearchInputFocused,
  };
}
